import React from 'react';
import { Brain, TrendingUp, AlertTriangle, Lightbulb, Target, DollarSign, Globe, FileText, Settings } from 'lucide-react';

const AnalyticsAIInsights = ({ 
  currentMetrics = {}, 
  comparisonMetrics = {}, 
  comparisonEnabled = false,
  loading = false 
}) => {
  const calculatePercentageChange = (current, previous) => {
    if (!previous || previous === 0) return null;
    return ((current - previous) / previous) * 100;
  };

  const generateInsights = () => {
    if (!comparisonEnabled || !currentMetrics || !comparisonMetrics) {
      return [];
    }

    const insights = [];
    const costChange = calculatePercentageChange(currentMetrics.cost || 0, comparisonMetrics.cost || 0);
    const clicksChange = calculatePercentageChange(currentMetrics.clicks || 0, comparisonMetrics.clicks || 0);
    const impressionsChange = calculatePercentageChange(currentMetrics.impressions || 0, comparisonMetrics.impressions || 0);
    const cpcChange = calculatePercentageChange(currentMetrics.cpc || currentMetrics.average_cpc || 0, comparisonMetrics.cpc || comparisonMetrics.average_cpc || 0);
    const ctrChange = calculatePercentageChange(currentMetrics.ctr || 0, comparisonMetrics.ctr || 0);

    // 1. Modificaciones de presupuestos
    if (costChange && costChange > 20) {
      insights.push({
        type: 'budget',
        priority: 'high',
        title: 'Ajuste de Presupuesto Recomendado',
        description: `Tu inversión aumentó ${costChange.toFixed(1)}% pero los resultados no crecieron proporcionalmente. Considera reducir el presupuesto o reasignarlo a campañas más efectivas.`,
        icon: DollarSign,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        action: 'Revisar distribución de presupuesto'
      });
    } else if (costChange && costChange < -20) {
      insights.push({
        type: 'budget',
        priority: 'medium',
        title: 'Oportunidad de Inversión',
        description: `Tu inversión disminuyó ${Math.abs(costChange).toFixed(1)}%. Si los resultados son positivos, considera aumentar el presupuesto para escalar el éxito.`,
        icon: TrendingUp,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        action: 'Evaluar aumento de presupuesto'
      });
    }

    // 2. Modificación de anuncios
    if (ctrChange && ctrChange < -10) {
      insights.push({
        type: 'ads',
        priority: 'high',
        title: 'Optimización de Anuncios Urgente',
        description: `Tu CTR disminuyó ${Math.abs(ctrChange).toFixed(1)}%. Los usuarios no están haciendo clic en tus anuncios. Revisa el copy, imágenes y relevancia.`,
        icon: Target,
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        action: 'Revisar creativos y copy'
      });
    } else if (ctrChange && ctrChange > 10) {
      insights.push({
        type: 'ads',
        priority: 'low',
        title: 'Anuncios Funcionando Bien',
        description: `Tu CTR mejoró ${ctrChange.toFixed(1)}%. Mantén el contenido actual y considera aplicar las mismas estrategias a otras campañas.`,
        icon: TrendingUp,
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        action: 'Replicar estrategias exitosas'
      });
    }

    // 3. Creación de nuevas campañas
    if (impressionsChange && impressionsChange < -15) {
      insights.push({
        type: 'campaigns',
        priority: 'medium',
        title: 'Expandir Alcance',
        description: `Las impresiones disminuyeron ${Math.abs(impressionsChange).toFixed(1)}%. Considera crear nuevas campañas o expandir el targeting para llegar a más audiencia.`,
        icon: Globe,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        action: 'Crear campañas adicionales'
      });
    }

    // 4. Revisión de la oferta comercial
    if (cpcChange && cpcChange > 15) {
      insights.push({
        type: 'offer',
        priority: 'high',
        title: 'Revisar Oferta Comercial',
        description: `Tu CPC aumentó ${cpcChange.toFixed(1)}%. Los costos por clic están subiendo. Revisa si tu oferta sigue siendo competitiva y atractiva.`,
        icon: FileText,
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        action: 'Evaluar competitividad de oferta'
      });
    }

    // 5. Revisión de la salud de la página
    if (clicksChange && clicksChange > 20 && ctrChange && ctrChange < 0) {
      insights.push({
        type: 'landing',
        priority: 'medium',
        title: 'Revisar Página de Destino',
        description: `Los clicks aumentaron ${clicksChange.toFixed(1)}% pero el CTR bajó. Los usuarios hacen clic pero pueden no estar satisfechos con la experiencia.`,
        icon: Settings,
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        action: 'Optimizar página de destino'
      });
    }

    // Insight general si no hay cambios significativos
    if (insights.length === 0) {
      insights.push({
        type: 'general',
        priority: 'low',
        title: 'Rendimiento Estable',
        description: 'Tus métricas se mantienen estables. Considera experimentar con nuevas estrategias para mejorar el rendimiento.',
        icon: Lightbulb,
        color: 'text-blue-600',
        bgColor: 'bg-blue-50',
        action: 'Explorar nuevas estrategias'
      });
    }

    return insights;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'border-l-4 border-l-red-500';
      case 'medium': return 'border-l-4 border-l-yellow-500';
      case 'low': return 'border-l-4 border-l-green-500';
      default: return 'border-l-4 border-l-gray-500';
    }
  };

  const insights = generateInsights();

  if (loading) {
    return (
      <div className="mt-8">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight mb-4">
          <span className="bg-gradient-to-r from-fuchsia-400 to-blue-700 bg-clip-text text-transparent">
            Análisis IA
          </span>
        </h2>
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="animate-pulse space-y-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gray-200 rounded-full" />
              <div className="h-4 bg-gray-200 rounded w-48" />
            </div>
            <div className="space-y-2">
              <div className="h-3 bg-gray-200 rounded w-full" />
              <div className="h-3 bg-gray-200 rounded w-3/4" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!comparisonEnabled) {
    return (
      <div className="mt-8">
        <h2 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight mb-4">
          <span className="bg-gradient-to-r from-fuchsia-400 to-blue-700 bg-clip-text text-transparent">
            Análisis IA
          </span>
        </h2>
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-3 text-gray-500">
            <Brain className="w-6 h-6" />
            <p className="text-sm">Activa la comparación de períodos para recibir recomendaciones inteligentes</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-8">
      <h2 className="text-lg sm:text-xl md:text-2xl font-bold tracking-tight mb-4">
        <span className="bg-gradient-to-r from-fuchsia-400 to-blue-700 bg-clip-text text-transparent">
          Análisis IA
        </span>
      </h2>
      
      <div className="space-y-4">
        {insights.map((insight, index) => (
          <div 
            key={index} 
            className={`bg-white border border-gray-200 rounded-2xl p-4 shadow-sm ${getPriorityColor(insight.priority)}`}
          >
            <div className="flex items-start gap-3">
              <div className={`p-2 rounded-lg ${insight.bgColor}`}>
                <insight.icon className={`w-5 h-5 ${insight.color}`} />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-semibold text-gray-900 text-sm">
                    {insight.title}
                  </h3>
                  {insight.priority === 'high' && (
                    <AlertTriangle className="w-4 h-4 text-red-500" />
                  )}
                </div>
                
                <p className="text-sm text-gray-600 mb-3">
                  {insight.description}
                </p>
                
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-primary">
                    💡 {insight.action}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AnalyticsAIInsights; 